// calendarFieldUpdate.js — build updated temporal field values after calendar DnD

import {
  addDays,
  addMilliseconds,
  differenceInMilliseconds,
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
} from 'date-fns';

import { timestampFromDate, normalizeDateValue } from 'utilities/DateTime';
import { compareCalendarEventItems, getCalendarTypeKey } from 'views/calendar/shared/calendarUtils';

export function getCalendarFieldStorageKey(node, field, typeKey) {
  const sourceKey = String(field?.source || typeKey || node?.type || '').trim().toLowerCase();
  if (!sourceKey) return null;
  return `data_${sourceKey}`;
}

export function getCalendarFieldSectionData(node, storageKey) {
  if (!node || !storageKey) return {};
  const section = node?.[storageKey];
  return section && typeof section === 'object' && !Array.isArray(section)
    ? { ...section }
    : {};
}

function readRangeStart(rawValue) {
  if (!rawValue || typeof rawValue !== 'object') return null;
  return normalizeDateValue(rawValue.startDate ?? rawValue);
}

function readRangeEnd(rawValue) {
  if (!rawValue || typeof rawValue !== 'object') return null;
  return normalizeDateValue(rawValue.endDate);
}

function shiftDatePreservingClock(sourceDate, targetDay) {
  const source = normalizeDateValue(sourceDate);
  const day = startOfDay(targetDay);
  if (!source || !day) return null;

  return setSeconds(
    setMinutes(
      setHours(day, source.getHours()),
      source.getMinutes(),
    ),
    source.getSeconds(),
  );
}

function shiftRangeByDayDelta(rawValue, targetDay) {
  const start = readRangeStart(rawValue);
  if (!start) return null;

  const end = readRangeEnd(rawValue);
  const targetStartDay = startOfDay(targetDay);
  const sourceStartDay = startOfDay(start);
  const dayDeltaMs = targetStartDay.getTime() - sourceStartDay.getTime();
  const nextStart = new Date(start.getTime() + dayDeltaMs);
  const nextEnd = end ? new Date(end.getTime() + dayDeltaMs) : null;
  const hasEnd = Boolean(nextEnd);

  if (hasEnd) {
    return {
      startDate: timestampFromDate(nextStart),
      startPrecision: rawValue.startPrecision || 'date',
      endDate: timestampFromDate(nextEnd),
      endPrecision: rawValue.endPrecision || rawValue.startPrecision || 'date',
      isRange: true,
    };
  }

  return {
    startDate: timestampFromDate(nextStart),
    startPrecision: rawValue.startPrecision || 'date',
    endDate: null,
    endPrecision: null,
    isRange: false,
  };
}

/**
 * Builds the stored field value after dropping a calendar event onto a day or time slot.
 */
export function buildCalendarFieldValueAfterMove({
  fieldType,
  rawValue,
  targetDate,
  targetHour = null,
  startPrecision = null,
} = {}) {
  const normalizedType = String(fieldType || '').toLowerCase();
  const safeTarget = normalizeDateValue(targetDate);
  if (!safeTarget) return null;

  if (normalizedType === 'annualdate') {
    return {
      month: safeTarget.getMonth() + 1,
      day: safeTarget.getDate(),
    };
  }

  if (normalizedType === 'date') {
    return {
      startDate: timestampFromDate(startOfDay(safeTarget)),
      startPrecision: 'date',
      endDate: null,
      endPrecision: null,
      isRange: false,
    };
  }

  if (normalizedType === 'daterange') {
    const start = readRangeStart(rawValue);
    const end = readRangeEnd(rawValue);
    const targetStartDay = startOfDay(safeTarget);
    const sourceStartDay = start ? startOfDay(start) : targetStartDay;
    const dayDeltaMs = targetStartDay.getTime() - sourceStartDay.getTime();
    const nextStart = start ? new Date(start.getTime() + dayDeltaMs) : startOfDay(safeTarget);
    const nextEnd = end
      ? new Date(end.getTime() + dayDeltaMs)
      : addDays(nextStart, 1);

    return {
      startDate: timestampFromDate(nextStart),
      startPrecision: 'date',
      endDate: timestampFromDate(nextEnd),
      endPrecision: 'date',
      isRange: true,
    };
  }

  if (normalizedType === 'datetime') {
    const currentStart = readRangeStart(rawValue) || normalizeDateValue(rawValue);
    let nextDate = startOfDay(safeTarget);

    if (Number.isInteger(targetHour)) {
      nextDate = setMinutes(setHours(nextDate, targetHour), currentStart?.getMinutes() || 0);
    } else if (currentStart && (startPrecision === 'time' || rawValue?.startPrecision === 'time')) {
      nextDate = shiftDatePreservingClock(currentStart, safeTarget);
    } else {
      nextDate = startOfDay(safeTarget);
    }

    return {
      startDate: timestampFromDate(nextDate),
      startPrecision: Number.isInteger(targetHour) || rawValue?.startPrecision === 'time' || startPrecision === 'time'
        ? 'time'
        : 'date',
      endDate: null,
      endPrecision: null,
      isRange: false,
    };
  }

  if (normalizedType === 'datetimerange') {
    const start = readRangeStart(rawValue);
    const end = readRangeEnd(rawValue);
    const durationMs = start && end
      ? Math.max(differenceInMilliseconds(end, start), 15 * 60 * 1000)
      : 60 * 60 * 1000;

    let nextStart = startOfDay(safeTarget);
    if (Number.isInteger(targetHour)) {
      nextStart = setMinutes(setHours(nextStart, targetHour), start?.getMinutes() || 0);
    } else if (start) {
      nextStart = shiftDatePreservingClock(start, safeTarget) || nextStart;
    }

    const nextEnd = addMilliseconds(nextStart, durationMs);

    return {
      startDate: timestampFromDate(nextStart),
      startPrecision: 'time',
      endDate: timestampFromDate(nextEnd),
      endPrecision: 'time',
      isRange: true,
      ...(rawValue?.recurrence ? { recurrence: rawValue.recurrence } : {}),
    };
  }

  return shiftRangeByDayDelta(rawValue, safeTarget);
}

export function applyOptimisticCalendarFieldValues(nodes, optimisticValues, typeDef) {
  if (!Array.isArray(nodes) || !optimisticValues || typeof optimisticValues !== 'object') {
    return nodes;
  }

  const overrideEntries = Object.entries(optimisticValues);
  if (!overrideEntries.length) return nodes;

  return nodes.map((node) => {
    const nodeKey = String(node?.key || node?.id || '').trim();
    if (!nodeKey) return node;

    const nodeOverrides = overrideEntries.filter(([key]) => key.startsWith(`${nodeKey}|`));
    if (!nodeOverrides.length) return node;

    let nextNode = { ...node };
    nodeOverrides.forEach(([key, value]) => {
      const [, storageKey, fieldName] = key.split('|');
      if (!storageKey || !fieldName) return;
      const section = getCalendarFieldSectionData(nextNode, storageKey);
      nextNode = {
        ...nextNode,
        [storageKey]: {
          ...section,
          [fieldName]: value,
        },
      };
    });

    return nextNode;
  });
}

export function buildCalendarOptimisticKey(nodeKey, storageKey, fieldName) {
  return `${nodeKey}|${storageKey}|${fieldName}`;
}

export function extractCalendarDateFromFieldValue(rawValue, fieldType, fallbackDate = null) {
  const normalizedType = String(fieldType || '').toLowerCase();

  if (normalizedType === 'annualdate') {
    const month = Number(rawValue?.month);
    const day = Number(rawValue?.day);
    if (!Number.isInteger(month) || !Number.isInteger(day)) return null;

    const year = fallbackDate instanceof Date && !Number.isNaN(fallbackDate.getTime())
      ? fallbackDate.getFullYear()
      : new Date().getFullYear();

    const nextDate = new Date(year, month - 1, day);
    return Number.isNaN(nextDate.getTime()) ? null : nextDate;
  }

  if (rawValue && typeof rawValue === 'object' && rawValue.startDate) {
    return normalizeDateValue(rawValue.startDate);
  }

  return normalizeDateValue(rawValue);
}

export function buildEventItemAfterFieldMove(item, nextValue, typeDef) {
  if (!item || !nextValue) return item;

  const fieldType = String(item?.field?.type || '').toLowerCase();
  const nextDate = extractCalendarDateFromFieldValue(nextValue, fieldType, item?.date);
  if (!nextDate) return item;

  const typeKey = getCalendarTypeKey(typeDef) || item?.node?.type || '';
  const storageKey = getCalendarFieldStorageKey(item.node, item.field, typeKey);
  const nextNode = storageKey && item.node
    ? {
      ...item.node,
      [storageKey]: {
        ...getCalendarFieldSectionData(item.node, storageKey),
        [item.field.name]: nextValue,
      },
    }
    : item.node;

  return {
    ...item,
    node: nextNode,
    rawValue: nextValue,
    date: nextDate,
    day: startOfDay(nextDate),
    startPrecision: nextValue?.startPrecision ?? item.startPrecision,
    endPrecision: nextValue?.endPrecision ?? item.endPrecision,
    isRange: Boolean(nextValue?.isRange ?? item.isRange),
  };
}

export function applyOptimisticCalendarEventItems(eventItems, optimisticValues, typeDef) {
  if (!Array.isArray(eventItems) || !optimisticValues || typeof optimisticValues !== 'object') {
    return eventItems;
  }

  const overrideEntries = Object.entries(optimisticValues);
  if (!overrideEntries.length) return eventItems;

  const nextItems = eventItems.map((item) => {
    const nodeKey = String(item?.node?.key || '').trim();
    const fieldName = String(item?.field?.name || '').trim();
    if (!nodeKey || !fieldName) return item;

    const typeKey = getCalendarTypeKey(typeDef) || item?.node?.type || '';
    const storageKey = getCalendarFieldStorageKey(item.node, item.field, typeKey);
    if (!storageKey) return item;

    const optimisticKey = buildCalendarOptimisticKey(nodeKey, storageKey, fieldName);
    const nextValue = optimisticValues[optimisticKey];
    if (!nextValue) return item;

    return buildEventItemAfterFieldMove(item, nextValue, typeDef);
  });

  return nextItems.sort(compareCalendarEventItems);
}
